/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

// react-range uses props in a way that is incompatible with our linter
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Range } from 'react-range';
import { ThemeContext } from '@/contexts/theme-context';
import { LocaleContext } from '@/contexts/locale-context';
import { TelemetryContext } from '@/contexts/telemetry-context';
import { AuthContext } from '@/contexts/auth-context';

export default function Slider({
  inputSelectorId,
  minimumValue,
  maximumValue,
  step,
  defaultValue,
  onChange,
  title,
  telemetryEvent,
  ariaLabel,
  permissionId,
}) {
  const { primaryColor } = useContext(ThemeContext);
  const { translate, textDirection } = useContext(LocaleContext);
  const telemetryContext = useContext(TelemetryContext);
  const [values, setValues] = useState([defaultValue]);

  const { hasPermission } = useContext(AuthContext);
  const isDisabledByRbac = permissionId ? !hasPermission(permissionId) : false;

  const valueChanged = (newValue) => {
    setValues(newValue);
    if (onChange) onChange(newValue[0]);
    if (telemetryEvent) telemetryContext?.sendEvent(telemetryEvent);
  };

  return (
    <div className="wrapper">
      {title && <div className="title">{translate(title)}</div>}
      <div>
        <Range
          aria-label={ariaLabel}
          id={inputSelectorId}
          min={minimumValue}
          max={maximumValue}
          values={values}
          step={step}
          rtl={textDirection === 'rtl'}
          disabled={isDisabledByRbac}
          renderTrack={({ props, children, disabled }) => (
            <div
              {...props}
              style={{
                ...props.style,
                backgroundColor: 'var(--color-light-grey)',
                borderRadius: '2px',
                height: '4px',
                display: 'flex',
                width: '100%',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div
              {...props}
              style={{
                ...props.style,
                height: '32px',
                width: '32px',
                lineHeight: 1.2,
                textAlign: 'center',
                backgroundColor: primaryColor,
                color: 'var(--color-white)',
                borderRadius: '50%',
                cursor: isDisabledByRbac ? 'default' : 'grab',
                padding: '8px 0',
                zIndex: '0 !important',
                fontSize: '14px',
                opacity: isDisabledByRbac ? 0.5 : 1,
              }}
            >
              {values}
            </div>
          )}
          onChange={(v) => valueChanged(v)}
        />
      </div>
      <div className={`track-label ${isDisabledByRbac ? 'disabled' : ''}`}>
        <div className="min-value">{`${minimumValue}%`}</div>
        <div className="max-value">{`${maximumValue}%`}</div>
      </div>
      <style jsx>
        {`
          .wrapper {
            display: flex;
            flex-direction: column;
            width: 100%;
          }

          .title {
            margin-bottom: 24px;
            font-weight: var(--font-weight-H1);
          }

          .track-label {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            width: 100%;
            margin-top: 20px;
          }

          .track-label.disabled {
            opacity: 0.3;
          }
        `}
      </style>
    </div>
  );
}

Slider.defaultProps = {
  inputSelectorId: null,
  minimumValue: 0,
  maximumValue: 100,
  step: 1,
  defaultValue: 50,
  onChange: null,
  telemetryEvent: null,
};

Slider.propTypes = {
  inputSelectorId: PropTypes.string,
  minimumValue: PropTypes.number,
  maximumValue: PropTypes.number,
  step: PropTypes.number,
  defaultValue: PropTypes.number,
  onChange: PropTypes.func,
  telemetryEvent: PropTypes.string,
};
